<template>
  <div class="sales-by-category">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 4">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-coin"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Revenue</div>
          <div class="rp-card-value">{{ formatMoney(summary.revenue) }}</div>
          <div class="rp-card-sub">{{ formatQty(summary.qty) }} units net · {{ plural(summary.invoice_count, 'invoice') }}</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-category"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Categories</div>
          <div class="rp-card-value">{{ formatInt(summary.category_count) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.sub_category_count, 'sub-category') }} · {{ plural(summary.product_count, 'product') }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-receipt-tax"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Cost</div>
          <div class="rp-card-value">{{ formatMoney(summary.cost) }}</div>
          <div class="rp-card-sub">returns {{ summary.return_rate }}% of sales</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-trending-up"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Gross Profit</div>
          <div class="rp-card-value" :class="netClass(summary.profit)">{{ formatMoney(summary.profit) }}</div>
          <div class="rp-card-sub">{{ summary.margin }}% margin</div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Revenue share</div>
        <template v-if="shareEntries.length">
          <div class="sc-doughnut"><canvas ref="shareChart"></canvas></div>
          <ul class="sc-legend">
            <li v-for="entry in shareEntries" :key="entry.label">
              <i class="rp-dot" :style="{ background: entry.color }"></i>
              <span class="text-truncate me-2">{{ entry.label }}</span>
              <span class="ms-auto fw-semibold">{{ entry.share }}%</span>
            </li>
          </ul>
        </template>
        <p v-else class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Revenue &amp; profit by category</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: SALES_COLOR }"></i>Revenue</span>
            <span><i class="rp-dot" :style="{ background: PROFIT_COLOR }"></i>Profit</span>
          </span>
        </div>
        <div v-show="rows.length" class="rp-chart" :style="{ height: `${chartHeight}px` }"><canvas ref="barChart"></canvas></div>
        <p v-if="!rows.length" class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Margin by category</div>
        <div v-if="marginBars.length" class="rp-bars">
          <div v-for="row in marginBars" :key="row.key">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ row.category }}</span>
              <span class="fw-semibold text-nowrap" :class="netClass(row.profit)">{{ row.margin }}%</span>
            </div>
            <div class="rp-bar-track">
              <div class="rp-bar-fill" :class="marginTone(row.margin)" :style="{ width: `${row.percent}%` }"></div>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No sales in this period.</p>
      </div>
    </div>

    <!-- Categories table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold text-body">
          <i class="ti ti-category me-1"></i>Categories
          <span class="rp-pill-count ms-1">{{ formatInt(rows.length) }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button type="button" class="rp-expand" :title="expanded[row.key] ? 'Hide sub-categories' : 'Show sub-categories'"
                          @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td>
                  <div class="fw-semibold">{{ row.category }}</div>
                  <span class="rp-sub text-truncate" :title="row.top_product">
                    {{ plural(row.sub_categories.length, 'sub-category') }}<template v-if="row.top_product"> · top: {{ row.top_product }}</template>
                  </span>
                </td>
                <td class="text-center"><span class="rp-count">{{ row.product_count }}</span></td>
                <td class="text-end fw-semibold">{{ formatQty(row.qty_sold) }}</td>
                <td class="text-end" :class="Number(row.returned_qty) ? 'text-danger' : 'text-muted'">
                  {{ Number(row.returned_qty) ? formatQty(row.returned_qty) : '—' }}
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.revenue) }}</td>
                <td class="text-end">{{ formatMoney(row.cost) }}</td>
                <td class="text-end fw-semibold" :class="netClass(row.profit)">{{ formatMoney(row.profit) }}</td>
                <td class="text-end"><span class="rp-badge" :class="marginTone(row.margin)">{{ row.margin }}%</span></td>
                <td class="text-end sc-share">
                  {{ row.share }}%
                  <div class="rp-bar-track mt-1"><div class="rp-bar-fill" :style="{ width: `${Math.min(100, Number(row.share))}%` }"></div></div>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title"><i class="ti ti-category-2 me-1"></i>{{ row.category }} — sub-categories</div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Sub-category</th>
                            <th>Top product</th>
                            <th class="text-end">Products</th>
                            <th class="text-end">Sold</th>
                            <th class="text-end">Returned</th>
                            <th class="text-end">Revenue</th>
                            <th class="text-end">Cost</th>
                            <th class="text-end">Gross Profit</th>
                            <th class="text-end">Margin</th>
                            <th class="text-end">Of category</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="sub in row.sub_categories" :key="sub.name">
                            <td class="fw-semibold">{{ sub.name }}</td>
                            <td class="text-muted">{{ sub.top_product || '—' }}</td>
                            <td class="text-end">{{ sub.product_count }}</td>
                            <td class="text-end">{{ formatQty(sub.qty_sold) }}</td>
                            <td class="text-end">{{ Number(sub.returned_qty) ? formatQty(sub.returned_qty) : '—' }}</td>
                            <td class="text-end fw-semibold">{{ formatMoney(sub.revenue) }}</td>
                            <td class="text-end">{{ formatMoney(sub.cost) }}</td>
                            <td class="text-end" :class="netClass(sub.profit)">{{ formatMoney(sub.profit) }}</td>
                            <td class="text-end">{{ sub.margin }}%</td>
                            <td class="text-end">{{ sub.share }}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-category"></i>
                  <div class="fw-semibold">No sales found</div>
                  <small class="text-muted">Try another period, customer or category.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th class="text-end">Total ({{ plural(rows.length, 'category') }})</th>
              <th class="text-center">{{ formatInt(summary.product_count) }}</th>
              <th class="text-end">{{ formatQty(summary.qty_sold) }}</th>
              <th class="text-end text-danger">{{ formatQty(summary.returned_qty) }}</th>
              <th class="text-end">{{ formatMoney(summary.revenue) }}</th>
              <th class="text-end">{{ formatMoney(summary.cost) }}</th>
              <th class="text-end" :class="netClass(summary.profit)">{{ formatMoney(summary.profit) }}</th>
              <th class="text-end">{{ summary.margin }}%</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Revenue = sales after discount, before VAT, less what was returned in the period. Expand a category for its
      sub-categories.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { PALETTE, PROFIT_COLOR, SALES_COLOR, marginTone } from './salesReportMixin';

const EMPTY_SUMMARY = {
  qty_sold: 0, returned_qty: 0, qty: 0, revenue: 0, cost: 0, profit: 0, category_count: 0, sub_category_count: 0,
  product_count: 0, invoice_count: 0, margin: 0, return_rate: 0, top_category: '', best_margin: '',
};
const SHARE_SLICES = 6;

export default {
  name: 'SalesByCategoryReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.sales_by_category_report.get,
  fileName: 'sales_by_category',
  columns: [
    { key: 'category', label: 'Category' },
    { key: 'product_count', label: 'Products', class: 'text-center', numeric: true },
    { key: 'qty_sold', label: 'Sold', class: 'text-end', numeric: true },
    { key: 'returned_qty', label: 'Returned', class: 'text-end', numeric: true },
    { key: 'revenue', label: 'Revenue', class: 'text-end', numeric: true },
    { key: 'cost', label: 'Cost', class: 'text-end', numeric: true },
    { key: 'profit', label: 'Gross Profit', class: 'text-end', numeric: true },
    { key: 'margin', label: 'Margin', class: 'text-end', numeric: true },
    { key: 'share', label: 'Share', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), customer: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      SALES_COLOR,
      PROFIT_COLOR,
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive || !!this.filters.customer;
    },

    /** The biggest categories, the rest summed as "Others". */
    shareEntries() {
      const earning = this.rows.filter((r) => Number(r.revenue) > 0);
      const total = earning.reduce((sum, r) => sum + Number(r.revenue), 0);
      const head = earning.slice(0, SHARE_SLICES - 1);
      const rest = earning.slice(SHARE_SLICES - 1);
      const entries = head.map((r) => ({ label: r.category, value: Number(r.revenue) }));
      if (rest.length === 1) entries.push({ label: rest[0].category, value: Number(rest[0].revenue) });
      else if (rest.length) entries.push({ label: `Others (${rest.length})`, value: rest.reduce((s, r) => s + Number(r.revenue), 0) });
      return entries.map((e, i) => ({
        ...e, color: PALETTE[i % PALETTE.length], share: total ? ((e.value / total) * 100).toFixed(1) : '0.0',
      }));
    },

    marginBars() {
      const rows = this.rows.filter((r) => Number(r.revenue) > 0)
        .map((r) => ({ ...r, abs: Math.abs(Number(r.margin) || 0) }))
        .sort((a, b) => Number(b.margin) - Number(a.margin));
      return this.withPercent(rows, 'abs').slice(0, 8);
    },

    chartHeight() {
      return Math.max(170, Math.min(this.rows.length, 10) * 28 + 30);
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  methods: {
    marginTone,

    extraParams() {
      return { ...this.periodParams(), ...this.customerParams() };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), customer: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => {
        this.drawDoughnut('share', this.$refs.shareChart, this.shareEntries);
        const top = this.rows.slice(0, 10);
        this.drawBars('bars', this.$refs.barChart, top.map((r) => r.category), [
          { label: 'Revenue', data: top.map((r) => r.revenue), color: SALES_COLOR },
          { label: 'Profit', data: top.map((r) => r.profit), color: PROFIT_COLOR },
        ], { horizontal: true });
      });
    },

    printReport() {
      const s = this.summary;
      const line = (name, r, sub = false) => `
        <tr>
          <td>${sub ? '&nbsp;&nbsp;&nbsp;– ' : ''}${escapeHtml(name)}</td>
          <td class="num">${r.product_count}</td>
          <td class="num">${this.formatQty(r.qty_sold)}</td>
          <td class="num">${this.formatQty(r.returned_qty)}</td>
          <td class="num">${this.formatMoney(r.revenue)}</td>
          <td class="num">${this.formatMoney(r.cost)}</td>
          <td class="num">${this.formatMoney(r.profit)}</td>
          <td class="num">${r.margin}%</td>
          <td class="num">${r.share}%</td>
        </tr>`;
      const body = this.visibleRows.map((r) => line(`${r.category}`, r)
        + (r.sub_categories.length > 1 ? r.sub_categories.map((sub) => line(sub.name, sub, true)).join('') : '')).join('');
      const table = `<table><thead><tr><th>Category</th><th class="num">Products</th><th class="num">Sold</th>
          <th class="num">Returned</th><th class="num">Revenue</th><th class="num">Cost</th><th class="num">Gross Profit</th>
          <th class="num">Margin</th><th class="num">Share</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td class="num">Total</td><td class="num">${s.product_count}</td>
          <td class="num">${this.formatQty(s.qty_sold)}</td><td class="num">${this.formatQty(s.returned_qty)}</td>
          <td class="num">${this.formatMoney(s.revenue)}</td><td class="num">${this.formatMoney(s.cost)}</td>
          <td class="num">${this.formatMoney(s.profit)}</td><td class="num">${s.margin}%</td><td></td></tr></tfoot></table>`;
      this.openPrint(
        'Sales by Category',
        [
          [this.periodText(), this.customerText(), ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Revenue', this.formatMoney(s.revenue)],
          ['Categories', `${s.category_count} · ${this.plural(s.product_count, 'product')}`],
          ['Cost', this.formatMoney(s.cost)],
          ['Gross Profit', `${this.formatMoney(s.profit)} (${s.margin}%)`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.sc-doughnut {
  position: relative;
  height: 130px;
  margin-bottom: 0.75rem;
}

.sc-legend {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.8rem;
}

.sc-legend li {
  display: flex;
  align-items: center;
  min-width: 0;
}

.sc-share {
  min-width: 90px;
}

.sc-share .rp-bar-track {
  height: 5px;
}
</style>
